(() => {
  const confirmationKey = 'izy:localization-confirmed:v1';
  const dismissalKey = 'izy:localization-dismissed:v2';
  const retention = 180 * 24 * 60 * 60 * 1000;

  class IzyLocalizationPopup extends HTMLElement {
    constructor() {
      super();
      this.ready = new Promise(resolve => { this.resolveReady = resolve; });
    }

    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.dialog = this.querySelector('dialog');
      this.country = this.querySelector('[name="country_code"]');
      this.language = this.querySelector('[name="language_code"]');
      this.countries = new Set(Array.from(this.country.options, option => option.value));
      this.languages = new Set(Array.from(this.language.options, option => option.value));
      this.abortController = new AbortController();
      const options = { signal: this.abortController.signal };
      this.querySelector('[data-localization-close]').addEventListener('click', () => this.dismiss(), options);
      this.dialog.addEventListener('cancel', event => { event.preventDefault(); this.dismiss(); }, options);
      this.dialog.addEventListener('click', event => {
        if (event.target !== this.dialog) return;
        const box = this.dialog.getBoundingClientRect();
        if (event.clientX < box.left || event.clientX > box.right || event.clientY < box.top || event.clientY > box.bottom) this.dismiss();
      }, options);
      this.language.addEventListener('change', () => this.countryNames(), options);
      document.addEventListener('submit', event => this.rememberSubmission(event), { ...options, capture: true });
      if (window.Shopify?.designMode) {
        this.closest('.shopify-section')?.addEventListener('shopify:section:select', () => this.show(), options);
        this.closest('.shopify-section')?.addEventListener('shopify:section:deselect', () => this.dismiss(false), options);
        this.resolveReady();
      } else {
        this.initialize();
      }
    }

    disconnectedCallback() {
      this.abortController?.abort();
      window.IzyPopupQueue?.cancel(this);
      this.dialog?.close();
      document.documentElement.classList.remove('izy-localization-open');
      window.IzyPopupQueue?.finishLocalization(this);
      this.resolveReady();
    }

    confirmed() {
      for (const storageName of ['localStorage', 'sessionStorage']) {
        try {
          const saved = JSON.parse(window[storageName].getItem(confirmationKey));
          if (saved?.version === 1 && Number.isFinite(saved.at) && saved.at <= Date.now() && Date.now() - saved.at < retention && this.countries.has(saved.country) && this.languages.has(saved.language)) return saved.language;
        } catch (_) { /* Preference storage may be unavailable. */ }
      }
      try {
        const dismissed = JSON.parse(sessionStorage.getItem(dismissalKey));
        return this.languages.has(dismissed?.language) ? dismissed.language : false;
      } catch (_) { return false; }
    }

    async initialize() {
      if (this.dataset.enabled !== 'true' || typeof this.dialog.showModal !== 'function') {
        window.IzyPopupQueue?.finishLocalization(this);
        this.resolveReady();
        return;
      }
      window.IzyPopupQueue?.beginLocalization(this);
      const confirmedLanguage = this.confirmed();
      if (confirmedLanguage) {
        if (confirmedLanguage !== this.dataset.language) this.continueInLanguage(confirmedLanguage);
        else {
          window.IzyPopupQueue?.finishLocalization(this);
          this.resolveReady();
        }
        return;
      }
      let detected = null;
      let suggestedLanguage = null;
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 3000);
      try {
        const root = window.Shopify?.routes?.root || '/';
        const endpoint = new URL(`${root}browsing_context_suggestions.json`, location.origin);
        endpoint.searchParams.set('country[enabled]', 'true');
        endpoint.searchParams.set('language[enabled]', 'true');
        const response = await fetch(endpoint, { credentials: 'same-origin', signal: controller.signal, headers: { Accept: 'application/json' } });
        if (!response.ok) throw new Error('Localization unavailable');
        const context = await response.json();
        const country = context.detected_values?.country?.handle;
        if (typeof country === 'string' && /^[A-Z]{2}$/.test(country)) detected = country;
        suggestedLanguage = context.suggestions?.find(suggestion => suggestion.parts?.language)?.parts.language.handle;
      } catch (_) { /* Without detection, ask neutrally instead of assuming a country. */ }
      finally { clearTimeout(timeout); }
      if (!this.isConnected) return;
      if (detected === 'GR' || detected === 'CY') {
        window.IzyPopupQueue?.finishLocalization(this);
        this.resolveReady();
        return;
      }
      if (this.countries.has(detected)) this.country.value = detected;
      const browserLanguage = navigator.language.split('-')[0];
      if (this.languages.has(suggestedLanguage)) this.language.value = suggestedLanguage;
      else if (this.languages.has(browserLanguage)) this.language.value = browserLanguage;
      else if (this.languages.has('en')) this.language.value = 'en';
      this.suggestedLanguage = this.language.value;
      this.countryNames();
      this.show();
    }

    countryNames() {
      if (typeof Intl.DisplayNames !== 'function') return;
      try {
        const names = new Intl.DisplayNames([this.language.value], { type: 'region' });
        for (const option of this.country.options) option.textContent = `${names.of(option.value)} (${option.value})`;
      } catch (_) { /* Keep Shopify's native labels if locale names are unavailable. */ }
    }

    show() {
      const open = () => {
        if (!this.isConnected || this.dialog.open || typeof this.dialog.showModal !== 'function') return;
        this.dialog.showModal();
        document.documentElement.classList.add('izy-localization-open');
        this.country.focus({ preventScroll: true });
      };
      const close = () => new Promise(resolve => {
        if (!this.dialog.open) { resolve(); return; }
        this.dialog.addEventListener('close', resolve, { once: true });
        this.dialog.close();
        document.documentElement.classList.remove('izy-localization-open');
      });
      if (window.IzyPopupQueue) window.IzyPopupQueue.request(this, open, close, 30, true);
      else if (window.Shopify?.designMode) open();
      else this.resolveReady();
    }

    continueInLanguage(language) {
      if (this.dismissSubmitting || !this.languages.has(language)) return;
      // Keep the current country. Native localization does not grant cookie consent.
      this.dismissSubmitting = true;
      this.country.value = this.dataset.country;
      this.language.value = language;
      this.querySelector('form').requestSubmit();
    }

    dismiss(remember = true) {
      if (this.dismissSubmitting) return;
      if (remember && !window.Shopify?.designMode && this.suggestedLanguage && this.suggestedLanguage !== this.dataset.language) {
        try { sessionStorage.setItem(dismissalKey, JSON.stringify({ language: this.suggestedLanguage })); } catch (_) {}
        this.continueInLanguage(this.suggestedLanguage);
        return;
      }
      window.IzyPopupQueue?.cancel(this);
      this.dialog.close();
      document.documentElement.classList.remove('izy-localization-open');
      if (remember && !window.Shopify?.designMode) {
        try { sessionStorage.setItem(dismissalKey, JSON.stringify({ language: this.suggestedLanguage || this.dataset.language })); } catch (_) { /* Optional preference storage. */ }
      }
      setTimeout(() => window.IzyPopupQueue?.finishLocalization(this), 400);
      this.resolveReady();
    }

    rememberSubmission(event) {
      const form = event.target;
      if (!(form instanceof HTMLFormElement) || window.Shopify?.designMode || this.dismissSubmitting && form === this.querySelector('form')) return;
      const action = new URL(form.action, location.origin);
      if (action.origin !== location.origin || !/\/localization\/?$/.test(action.pathname) || event.defaultPrevented) return;
      const fields = new FormData(form);
      if (event.submitter?.name) fields.set(event.submitter.name, event.submitter.value);
      const country = fields.get('country_code') || this.dataset.country;
      // Prestige's native selectors submit locale_code; the welcome popup uses language_code.
      const language = fields.get('language_code') || fields.get('locale_code') || this.dataset.language;
      if (!this.countries.has(country) || !this.languages.has(language)) return;
      try { sessionStorage.removeItem(dismissalKey); } catch (_) { /* Optional preference storage. */ }
      const value = JSON.stringify({ version: 1, country, language, at: Date.now() });
      for (const storageName of ['localStorage', 'sessionStorage']) {
        try { window[storageName].setItem(confirmationKey, value); } catch (_) { /* Try session storage next. */ }
      }
    }
  }

  if (!customElements.get('izy-localization-popup')) customElements.define('izy-localization-popup', IzyLocalizationPopup);

  const resumeNewsletters = () => {
    const popup = document.querySelector('izy-localization-popup');
    if (!window.Shopify?.designMode && !window.IzyPopupQueue?.newsletterEligible) return;
    Promise.resolve(popup?.ready).then(() => {
      for (const template of document.querySelectorAll('template[data-izy-localization-newsletter]')) {
        template.replaceWith(template.content.cloneNode(true));
      }
    });
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', resumeNewsletters, { once: true });
  else resumeNewsletters();
})();
